import { useEffect, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { useAuth } from "auth/AuthProvider";
import articleService from "services/articleService";
import uploadService from "services/uploadService";

// ⚠️ important: CSS import global dans index.js ou App.js
import ReactQuill from "react-quill";

export default function CreateArticleDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const defaultAuthor = user?.firstName || "Équipe";

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);

  // Champs du formulaire
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState(defaultAuthor);
  const [categoriesInput, setCategoriesInput] = useState("");
  const [content, setContent] = useState(""); // HTML du WYSIWYG

  useEffect(() => {
    if (open) {
      setSaving(false);
      setError("");
      setFiles([]);
      setTitle("");
      setAuthor(defaultAuthor);
      setCategoriesInput("");
      setContent("");
    }
  }, [open]);

  // Toolbar et formats (Quill)
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link"],
        ["clean"],
      ],
      clipboard: {
        // conserve le style raisonnablement simple
        matchVisual: false,
      },
    }),
    []
  );

  const quillFormats = useMemo(
    () => [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "indent",
      "align",
      "blockquote",
      "code-block",
      "link",
    ],
    []
  );

  async function onSubmit(e) {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Le titre est requis");
      return;
    }
    if (!content || !content.trim() || content === "<p><br></p>") {
      setError("Le contenu est requis");
      return;
    }

    setSaving(true);

    const categories = categoriesInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const payload = {
      title: title.trim(),
      content, // HTML direct depuis ReactQuill
      author: author?.trim() || defaultAuthor,
      categories,
    };

    try {
      // 1) créer l’article (slug généré côté back)
      const created = await articleService.createArticle(payload);

      // 2) upload médias vers dossier /uploads/articles/:slug
      if (files.length) {
        const uploaded = await uploadService.uploadArticleFiles(created.slug, files);
        const media = uploaded.map((f) => ({ url: f.url, label: f.label }));
        await articleService.updateArticleMedia(created.slug, media);
      }

      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={onSubmit}>
        <DialogTitle sx={{ fontWeight: 600 }}>Nouvel article</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                label="Titre"
                fullWidth
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Contenu
              </Typography>
              <Box
                sx={{
                  // coins arrondis & fond blanc
                  ".ql-toolbar": {
                    borderTopLeftRadius: 1,
                    borderTopRightRadius: 1,
                    backgroundColor: "white",
                  },
                  ".ql-container": {
                    borderBottomLeftRadius: 1,
                    borderBottomRightRadius: 1,
                    backgroundColor: "white",
                  },
                  // hauteur minimale de l’aire de saisie
                  ".ql-editor": { minHeight: 240, fontSize: 16, lineHeight: 1.6 },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={quillFormats}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Auteur"
                fullWidth
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Catégories (séparées par des virgules)"
                fullWidth
                value={categoriesInput}
                onChange={(e) => setCategoriesInput(e.target.value)}
                placeholder="actualité, association"
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" component="label" sx={{ color: "common.white" }}>
                Joindre des fichiers (images / PDF)
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => setFiles(Array.from(e.target.files || []))}
                />
              </Button>
              {files.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {files.length} fichier(s) sélectionné(s)
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              bgcolor: "grey.700",
              color: "common.white",
              "&:hover": { bgcolor: "grey.800" },
            }}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            sx={{ ml: 1, minWidth: 160, color: "common.white" }}
            disabled={saving}
            endIcon={saving ? <CircularProgress size={18} sx={{ color: "common.white" }} /> : null}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

CreateArticleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};
